import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import {
  getFreezeLimits,
  getLotSizes,
} from "../../services/generalDataCache";
import { fetchDerivedDataLatest } from "../../services/derivedData";
import {
  placeExitAllOrders,
  placeMoveOrder,
  placeOrder,
} from "../../services/orderService";
import {
  getSettings,
  updatePnlSettings,
  updateTrailingStop,
} from "../../services/settingsService";
import { isAuthenticated } from "../../services/auth";
import { useDerivedData, usePnLData } from "../../hooks/useMarketData";
import { useToast } from "../Toast/ToastContext";
import QuantityInput from "../common/QuantityInput";
import eventBus, { EVENTS } from "../../services/eventBus";
import { formatOrderError, formatOrderSuccess } from "../../utils/toastMessages";
import "./MobileHomeView.css";

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatNumber = (value, fractionDigits = 0) => {
  const number = toFiniteNumber(value) ?? 0;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(number);
};

const formatSignedAmount = (value) => {
  const number = toFiniteNumber(value) ?? 0;
  const sign = number >= 0 ? "+" : "-";
  return `${sign}Rs ${formatNumber(Math.abs(number), 0)}`;
};

const formatPrice = (value) => {
  const number = toFiniteNumber(value);
  if (number === null) return "--";
  return formatNumber(number, 2);
};

const parseRiskNumber = (value, fallback = 0) => {
  const number = parseInt(value, 10);
  return Number.isFinite(number) ? number : fallback;
};

const parseTrailingText = (value, fallbackPercent = 25, fallbackActivation = 500) => {
  const parts = String(value).match(/\d+/g) || [];
  return {
    percent: parseRiskNumber(parts[0], fallbackPercent),
    activation: parseRiskNumber(parts[1], fallbackActivation),
  };
};

const RISK_STEP_SIZE = 500;
const TRAILING_PERCENT_STEP_SIZE = 5;
const TRAILING_PERCENT_MIN = 5;
const TRAILING_PERCENT_MAX = 55;
const TRAILING_ACTIVATION_STEP_SIZE = 500;
const TRAILING_ACTIVATION_MIN = 0;

const clampNumber = (value, min, max) => {
  const number = toFiniteNumber(value) ?? min;
  return Math.min(max, Math.max(min, number));
};

const normalizeTrailingPercent = (value, fallback = 25) =>
  clampNumber(parseRiskNumber(value, fallback), TRAILING_PERCENT_MIN, TRAILING_PERCENT_MAX);

const normalizeTrailingActivation = (value, fallback = 500) =>
  Math.max(TRAILING_ACTIVATION_MIN, parseRiskNumber(value, fallback));

const formatCompactExpiry = (expiry) => {
  if (!expiry) return "";

  const parts = String(expiry).split("-");
  if (parts.length !== 3) return expiry;

  if (parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}`;
  }

  return `${parts[0]}/${parts[1]}`;
};

const getPositionIdentity = (position) =>
  position.instrument_token ||
  position.trading_symbol ||
  `${position.strike}-${position.option_type}-${position.expiry || ""}`;

const getMoveQuantityKey = (position, targetStrike) =>
  `${getPositionIdentity(position)}-${targetStrike}`;

const getMoveActionKey = (position, direction, targetStrike) =>
  `${getPositionIdentity(position)}-${direction}-${targetStrike}`;

const getAddActionKey = (position) => `${getPositionIdentity(position)}-add`;

const getIndexFromSymbol = (symbol) => {
  if (!symbol) return "NIFTY";
  if (symbol.startsWith("BANKNIFTY")) return "BANKNIFTY";
  if (symbol.startsWith("SENSEX")) return "SENSEX";
  if (symbol.startsWith("NIFTY")) return "NIFTY";
  return "NIFTY";
};

const DEFAULT_FREEZE_LIMITS = {
  NIFTY: 1800,
  BANKNIFTY: 900,
  SENSEX: 1000,
};

const getStepFraction = (stepSize) => {
  if (stepSize === "1/4") return 0.25;
  if (stepSize === "1/2") return 0.5;
  return 1;
};

const formatISTTime = (timestamp) => {
  const numericTimestamp = toFiniteNumber(timestamp);
  if (numericTimestamp === null || numericTimestamp <= 0) {
    return "Waiting for tick";
  }

  const milliseconds =
    numericTimestamp < 1000000000000
      ? numericTimestamp * 1000
      : numericTimestamp;

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(milliseconds));
};

const getPositionLegLabel = (position) => {
  const strike = toFiniteNumber(position.strike);
  const optionType = position.option_type || "";

  if (strike !== null && optionType) {
    return `${formatNumber(strike, 0)} ${optionType}`;
  }

  return position.trading_symbol || "Position";
};

const getPositionDiff = (position) => {
  const diff = toFiniteNumber(position.diff);
  if (diff !== null) return diff;

  const quantity = toFiniteNumber(position.quantity) ?? 0;
  const ltp = toFiniteNumber(position.ltp);
  const buyPrice = toFiniteNumber(position.buy_price);
  const sellPrice = toFiniteNumber(position.sell_price);

  if (ltp === null) return null;
  if (quantity < 0 && sellPrice !== null) return sellPrice - ltp;
  if (quantity >= 0 && buyPrice !== null) return ltp - buyPrice;

  return null;
};

const getPositionPnL = (position) => {
  const directPnL = toFiniteNumber(position.pnl);
  if (directPnL !== null) return directPnL;

  const quantity = toFiniteNumber(position.quantity) ?? 0;
  const ltp = toFiniteNumber(position.ltp);
  const sellValue = toFiniteNumber(position.sell_value) ?? 0;
  const buyValue = toFiniteNumber(position.buy_value) ?? 0;
  const multiplier = toFiniteNumber(position.multiplier) || 1;

  if (ltp === null) return null;

  if (sellValue !== 0 || buyValue !== 0) {
    return sellValue - buyValue + quantity * ltp * multiplier;
  }

  const buyPrice = toFiniteNumber(position.buy_price);
  const sellPrice = toFiniteNumber(position.sell_price);

  if (quantity < 0 && sellPrice !== null) {
    return Math.abs(quantity) * (sellPrice - ltp);
  }
  if (quantity >= 0 && buyPrice !== null) {
    return Math.abs(quantity) * (ltp - buyPrice);
  }

  return null;
};

const formatSignedNumber = (value) => {
  if (value === null || value === undefined || value === "") return "--";

  const number = toFiniteNumber(value);
  if (number === null) return "--";
  const roundedValue = Math.round(number);
  return `${roundedValue >= 0 ? "+" : ""}${formatNumber(roundedValue, 0)}`;
};

const getOptionSortRank = (position) => {
  if (position.option_type === "PE") return 0;
  if (position.option_type === "CE") return 1;
  return 2;
};

function MobileMoveTarget({
  position,
  direction,
  moveOption,
  selectedQuantity,
  lotSize,
  freezeLimit,
  suggestions,
  isMoving,
  onQuantityChange,
  onMove,
  getFractionalQuantity,
}) {
  const steps = Array.isArray(moveOption.steps) ? moveOption.steps : [];
  const commandLabel = direction === "away" ? ">" : "<";
  const targetLabel = direction === "away" ? "Away" : "Closer";

  return (
    <div className="mobile-move-target">
      <div className="mobile-move-target-main">
        <span className="mobile-move-strike">{moveOption.strike}</span>
        {moveOption.premium && (
          <span className="mobile-move-premium">{formatPrice(moveOption.premium)}</span>
        )}
      </div>
      <div className="mobile-move-actions">
        <QuantityInput
          value={selectedQuantity}
          onChange={onQuantityChange}
          lotSize={lotSize}
          freezeLimit={freezeLimit}
          suggestions={suggestions}
          containerClassName="mobile-move-quantity"
          compact={true}
          disabled={isMoving}
        />
        <button
          type="button"
          className={`mobile-move-submit mobile-move-submit--${direction}`}
          onClick={() => onMove(selectedQuantity)}
          disabled={isMoving}
          title={`${targetLabel} ${selectedQuantity} quantity to ${moveOption.strike}`}
          aria-label={`${targetLabel} ${selectedQuantity} quantity to ${moveOption.strike}`}
        >
          {isMoving ? "..." : commandLabel}
        </button>
        {steps.map((stepSize) => {
          const fractionalQuantity = getFractionalQuantity(position, stepSize);
          const disabled = isMoving || fractionalQuantity < lotSize;

          return (
            <button
              key={stepSize}
              type="button"
              className="mobile-move-step"
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  onMove(fractionalQuantity);
                }
              }}
              title={
                disabled
                  ? `${stepSize} is below one lot`
                  : `${targetLabel} ${fractionalQuantity} quantity`
              }
              aria-label={
                disabled
                  ? `${stepSize} ${targetLabel.toLowerCase()} unavailable`
                  : `${targetLabel} ${fractionalQuantity} quantity`
              }
            >
              {stepSize}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileMoveGroup({
  position,
  direction,
  options,
  getMoveQuantity,
  onMoveQuantityChange,
  onMovePosition,
  movingKey,
  getLotSizeForPosition,
  getFreezeLimitForPosition,
  generateQuantityOptions,
  getFractionalQuantity,
}) {
  if (!options.length) return null;

  const label = direction === "away" ? "Away" : "Closer";
  const lotSize = getLotSizeForPosition(position);
  const freezeLimit = getFreezeLimitForPosition(position);
  const suggestions = generateQuantityOptions(lotSize, freezeLimit);

  return (
    <div className={`mobile-move-group mobile-move-group--${direction}`}>
      <div className="mobile-move-group__label">{label}</div>
      <div className="mobile-move-targets">
        {options.map((moveOption, index) => {
          const selectedQuantity = getMoveQuantity(position, moveOption.strike);
          const actionKey = getMoveActionKey(position, direction, moveOption.strike);

          return (
            <MobileMoveTarget
              key={`${direction}-${moveOption.strike}-${index}`}
              position={position}
              direction={direction}
              moveOption={moveOption}
              selectedQuantity={selectedQuantity}
              lotSize={lotSize}
              freezeLimit={freezeLimit}
              suggestions={suggestions}
              isMoving={movingKey === actionKey}
              onQuantityChange={(value) =>
                onMoveQuantityChange(position, moveOption.strike, value)
              }
              onMove={(quantity) =>
                onMovePosition(position, direction, moveOption.strike, quantity)
              }
              getFractionalQuantity={getFractionalQuantity}
            />
          );
        })}
      </div>
    </div>
  );
}

function MobileAddGroup({
  position,
  selectedQuantity,
  lotSize,
  freezeLimit,
  suggestions,
  isAdding,
  onQuantityChange,
  onAddPosition,
}) {
  const action = (toFiniteNumber(position.quantity) ?? 0) > 0 ? "BUY" : "SELL";

  return (
    <div className="mobile-move-group mobile-move-group--add">
      <div className="mobile-move-group__label">Add</div>
      <div className="mobile-move-targets">
        <div className="mobile-move-target">
          <div className="mobile-move-target-main">
            <span className="mobile-move-strike">{action}</span>
            <span className="mobile-move-premium">{getPositionLegLabel(position)}</span>
          </div>
          <div className="mobile-move-actions">
            <QuantityInput
              value={selectedQuantity}
              onChange={onQuantityChange}
              lotSize={lotSize}
              freezeLimit={freezeLimit}
              suggestions={suggestions}
              containerClassName="mobile-move-quantity"
              compact={true}
              disabled={isAdding}
            />
            <button
              type="button"
              className="mobile-move-submit mobile-move-submit--add"
              onClick={() => onAddPosition(selectedQuantity)}
              disabled={isAdding}
              title={`Add ${selectedQuantity} quantity to ${position.trading_symbol}`}
              aria-label={`Add ${selectedQuantity} quantity to ${position.trading_symbol}`}
            >
              {isAdding ? "..." : "+"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobilePositionMovement({
  position,
  getAddQuantity,
  onAddQuantityChange,
  onAddPosition,
  getMoveQuantity,
  onMoveQuantityChange,
  onMovePosition,
  movingKey,
  getLotSizeForPosition,
  getFreezeLimitForPosition,
  generateQuantityOptions,
  getFractionalQuantity,
}) {
  const awayOptions = Array.isArray(position.moves?.away)
    ? position.moves.away
    : [];
  const closerOptions = Array.isArray(position.moves?.closer)
    ? position.moves.closer
    : [];
  const lotSize = getLotSizeForPosition(position);
  const freezeLimit = getFreezeLimitForPosition(position);
  const suggestions = generateQuantityOptions(lotSize, freezeLimit);
  const selectedAddQuantity = getAddQuantity(position);
  const addActionKey = getAddActionKey(position);

  if (!position.instrument_token && awayOptions.length === 0 && closerOptions.length === 0) {
    return null;
  }

  return (
    <tr className="mobile-position-movement-row">
      <td colSpan={4}>
        <div className="mobile-position-movement">
          {position.instrument_token && (
            <MobileAddGroup
              position={position}
              selectedQuantity={selectedAddQuantity}
              lotSize={lotSize}
              freezeLimit={freezeLimit}
              suggestions={suggestions}
              isAdding={movingKey === addActionKey}
              onQuantityChange={(value) => onAddQuantityChange(position, value)}
              onAddPosition={(quantity) => onAddPosition(position, quantity)}
            />
          )}
          <MobileMoveGroup
            position={position}
            direction="away"
            options={awayOptions}
            getMoveQuantity={getMoveQuantity}
            onMoveQuantityChange={onMoveQuantityChange}
            onMovePosition={onMovePosition}
            movingKey={movingKey}
            getLotSizeForPosition={getLotSizeForPosition}
            getFreezeLimitForPosition={getFreezeLimitForPosition}
            generateQuantityOptions={generateQuantityOptions}
            getFractionalQuantity={getFractionalQuantity}
          />
          <MobileMoveGroup
            position={position}
            direction="closer"
            options={closerOptions}
            getMoveQuantity={getMoveQuantity}
            onMoveQuantityChange={onMoveQuantityChange}
            onMovePosition={onMovePosition}
            movingKey={movingKey}
            getLotSizeForPosition={getLotSizeForPosition}
            getFreezeLimitForPosition={getFreezeLimitForPosition}
            generateQuantityOptions={generateQuantityOptions}
            getFractionalQuantity={getFractionalQuantity}
          />
        </div>
      </td>
    </tr>
  );
}

const formatMoveSummary = (position, getMoveQuantity) => {
  const awayOption = Array.isArray(position.moves?.away)
    ? position.moves.away[0]
    : null;
  const closerOption = Array.isArray(position.moves?.closer)
    ? position.moves.closer[0]
    : null;
  const firstTarget = awayOption || closerOption;
  const summaryParts = [];

  if (awayOption) {
    summaryParts.push(`A ${awayOption.strike}`);
  }

  if (closerOption) {
    summaryParts.push(`C ${closerOption.strike}`);
  }

  if (firstTarget) {
    summaryParts.push(`Q ${getMoveQuantity(position, firstTarget.strike)}`);
  }

  return summaryParts.join(" ");
};

function MobilePositionRow({
  position,
  isExpanded,
  onToggleMovement,
  getAddQuantity,
  onAddQuantityChange,
  onAddPosition,
  getMoveQuantity,
  onMoveQuantityChange,
  onMovePosition,
  movingKey,
  getLotSizeForPosition,
  getFreezeLimitForPosition,
  generateQuantityOptions,
  getFractionalQuantity,
}) {
  const quantity = toFiniteNumber(position.quantity) ?? 0;
  const diff = getPositionDiff(position);
  const positionPnL = getPositionPnL(position);
  const expiry = formatCompactExpiry(position.expiry);
  const side = quantity < 0 ? "SELL" : "BUY";
  const hasMoves =
    (Array.isArray(position.moves?.away) && position.moves.away.length > 0) ||
    (Array.isArray(position.moves?.closer) && position.moves.closer.length > 0);
  const hasDetails = hasMoves || Boolean(position.instrument_token);
  const moveSummary = hasMoves ? formatMoveSummary(position, getMoveQuantity) : "";

  return (
    <>
      <tr
        className={`mobile-position-row mobile-position-row--${
          (position.option_type || "unknown").toLowerCase()
        } ${hasDetails ? "is-clickable" : ""} ${isExpanded ? "is-expanded" : ""}`}
        onClick={hasDetails ? onToggleMovement : undefined}
        onKeyDown={(event) => {
          if (!hasDetails) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggleMovement();
          }
        }}
        tabIndex={hasDetails ? 0 : undefined}
        aria-expanded={hasDetails ? isExpanded : undefined}
      >
        <td className="mobile-position-leg">
          <span className="mobile-position-leg__name">
            {getPositionLegLabel(position)}
          </span>
          <span className="mobile-position-leg__meta">
            {side}
            {expiry ? ` - ${expiry}` : ""}
          </span>
          {moveSummary && (
            <span className="mobile-position-leg__moves">{moveSummary}</span>
          )}
          <span
            className={`mobile-position-leg__pnl ${
              (positionPnL ?? 0) >= 0 ? "is-profit" : "is-loss"
            }`}
          >
            P&L {formatSignedNumber(positionPnL)}
          </span>
        </td>
        <td className="mobile-position-number">
          {formatNumber(Math.abs(quantity), 0)}
        </td>
        <td className="mobile-position-number">{formatPrice(position.ltp)}</td>
        <td
          className={`mobile-position-number mobile-position-diff ${
            (diff ?? 0) >= 0 ? "is-profit" : "is-loss"
          }`}
        >
          {diff === null ? "--" : formatPrice(diff)}
        </td>
      </tr>
      {isExpanded && (
        <MobilePositionMovement
          position={position}
          getAddQuantity={getAddQuantity}
          onAddQuantityChange={onAddQuantityChange}
          onAddPosition={onAddPosition}
          getMoveQuantity={getMoveQuantity}
          onMoveQuantityChange={onMoveQuantityChange}
          onMovePosition={onMovePosition}
          movingKey={movingKey}
          getLotSizeForPosition={getLotSizeForPosition}
          getFreezeLimitForPosition={getFreezeLimitForPosition}
          generateQuantityOptions={generateQuantityOptions}
          getFractionalQuantity={getFractionalQuantity}
        />
      )}
    </>
  );
}

function MobileRiskSettings({
  disabled,
  exitPnlEnabled,
  exitPnlValue,
  targetPnlEnabled,
  targetPnlValue,
  trailingEnabled,
  trailingText,
  peakPnl,
  onExitToggle,
  onExitChange,
  onExitBlur,
  onExitStep,
  onTargetToggle,
  onTargetChange,
  onTargetBlur,
  onTargetStep,
  onTrailingToggle,
  onTrailingOpen,
}) {
  return (
    <div className="mobile-risk-strip" aria-label="Risk controls">
      <div className={`mobile-risk-chip ${exitPnlEnabled ? "is-active" : ""}`}>
        <input
          type="checkbox"
          checked={exitPnlEnabled}
          onChange={(event) => onExitToggle(event.target.checked)}
          disabled={disabled}
          aria-label="Enable stop loss PnL"
        />
        <span>SL</span>
        <button
          type="button"
          className="mobile-risk-step-button"
          onClick={() => onExitStep(-RISK_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Decrease stop loss by ${RISK_STEP_SIZE}`}
        >
          -
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={exitPnlValue}
          onChange={(event) => onExitChange(event.target.value)}
          onBlur={onExitBlur}
          disabled={disabled}
          aria-label="Stop loss PnL"
        />
        <button
          type="button"
          className="mobile-risk-step-button"
          onClick={() => onExitStep(RISK_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Increase stop loss by ${RISK_STEP_SIZE}`}
        >
          +
        </button>
      </div>

      <div className={`mobile-risk-chip ${targetPnlEnabled ? "is-active" : ""}`}>
        <input
          type="checkbox"
          checked={targetPnlEnabled}
          onChange={(event) => onTargetToggle(event.target.checked)}
          disabled={disabled}
          aria-label="Enable target PnL"
        />
        <span>TG</span>
        <button
          type="button"
          className="mobile-risk-step-button"
          onClick={() => onTargetStep(-RISK_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Decrease target by ${RISK_STEP_SIZE}`}
        >
          -
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={targetPnlValue}
          onChange={(event) => onTargetChange(event.target.value)}
          onBlur={onTargetBlur}
          disabled={disabled}
          aria-label="Target PnL"
        />
        <button
          type="button"
          className="mobile-risk-step-button"
          onClick={() => onTargetStep(RISK_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Increase target by ${RISK_STEP_SIZE}`}
        >
          +
        </button>
      </div>

      <div
        className={`mobile-risk-chip mobile-risk-chip--trail ${
          trailingEnabled ? "is-active" : ""
        }`}
        title={`Peak ${formatNumber(peakPnl, 0)}`}
      >
        <input
          type="checkbox"
          checked={trailingEnabled}
          onChange={(event) => onTrailingToggle(event.target.checked)}
          disabled={disabled}
          aria-label="Enable trailing stop"
        />
        <span>TR</span>
        <button
          type="button"
          className="mobile-risk-trail-button"
          onClick={onTrailingOpen}
          disabled={disabled}
          aria-label="Trailing stop percent and activation"
        >
          {trailingText}
        </button>
      </div>
    </div>
  );
}

function MobileTrailingEditor({
  disabled,
  percent,
  activation,
  onPercentChange,
  onActivationChange,
  onPercentStep,
  onActivationStep,
  onCancel,
  onSave,
}) {
  return (
    <div className="mobile-trailing-editor" aria-label="Trailing stop editor">
      <label>
        <span>Trail %</span>
        <button
          type="button"
          className="mobile-trailing-step-button"
          onClick={() => onPercentStep(-TRAILING_PERCENT_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Decrease trailing percent by ${TRAILING_PERCENT_STEP_SIZE}`}
        >
          -
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={percent}
          onChange={(event) => onPercentChange(event.target.value)}
          disabled={disabled}
          aria-label="Trailing percentage"
        />
        <button
          type="button"
          className="mobile-trailing-step-button"
          onClick={() => onPercentStep(TRAILING_PERCENT_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Increase trailing percent by ${TRAILING_PERCENT_STEP_SIZE}`}
        >
          +
        </button>
      </label>
      <label>
        <span>After</span>
        <button
          type="button"
          className="mobile-trailing-step-button"
          onClick={() => onActivationStep(-TRAILING_ACTIVATION_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Decrease trailing activation by ${TRAILING_ACTIVATION_STEP_SIZE}`}
        >
          -
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={activation}
          onChange={(event) => onActivationChange(event.target.value)}
          disabled={disabled}
          aria-label="Trailing activation profit"
        />
        <button
          type="button"
          className="mobile-trailing-step-button"
          onClick={() => onActivationStep(TRAILING_ACTIVATION_STEP_SIZE)}
          disabled={disabled}
          aria-label={`Increase trailing activation by ${TRAILING_ACTIVATION_STEP_SIZE}`}
        >
          +
        </button>
      </label>
      <button type="button" onClick={onCancel} disabled={disabled}>
        Cancel
      </button>
      <button
        type="button"
        className="mobile-trailing-save"
        onClick={onSave}
        disabled={disabled}
      >
        {disabled ? "Saving" : "Save"}
      </button>
    </div>
  );
}

export default function MobileHomeView({
  positions = {},
  summary = {},
  selectedIndex,
  selectedExpiry,
  defaultQuantity = 50,
  indexStreamData = {},
  onDefaultQuantityChange,
  onRefreshPositions,
}) {
  const { data: pnlStreamData, isConnected } = usePnLData(true);
  const { data: derivedStreamData } = useDerivedData(selectedIndex, !!selectedIndex);
  const { showError, showSuccess, showWarning } = useToast();
  const [initialPnL, setInitialPnL] = useState(null);
  const [latestDerivedData, setLatestDerivedData] = useState(null);
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isRiskLoading, setIsRiskLoading] = useState(false);
  const [exitPnlEnabled, setExitPnlEnabled] = useState(false);
  const [exitPnlValue, setExitPnlValue] = useState(-1000);
  const [targetPnlEnabled, setTargetPnlEnabled] = useState(false);
  const [targetPnlValue, setTargetPnlValue] = useState(2000);
  const [trailingEnabled, setTrailingEnabled] = useState(false);
  const [trailingPercent, setTrailingPercent] = useState(25);
  const [trailingActivation, setTrailingActivation] = useState(500);
  const [trailingText, setTrailingText] = useState("25@500");
  const [showTrailingEditor, setShowTrailingEditor] = useState(false);
  const [editTrailingPercent, setEditTrailingPercent] = useState(25);
  const [editTrailingActivation, setEditTrailingActivation] = useState(500);
  const [peakPnl, setPeakPnl] = useState(0);
  const [lotSizes, setLotSizes] = useState({});
  const [freezeLimits, setFreezeLimits] = useState({});
  const [addQuantities, setAddQuantities] = useState({});
  const [moveQuantities, setMoveQuantities] = useState({});
  const [movingKey, setMovingKey] = useState(null);
  const [expandedPositionKey, setExpandedPositionKey] = useState(null);

  const openPositions = useMemo(() => {
    const nextPositions = Array.isArray(positions.open) ? positions.open : [];

    return [...nextPositions].sort((a, b) => {
      const optionRank = getOptionSortRank(a) - getOptionSortRank(b);
      if (optionRank !== 0) return optionRank;

      const strikeA = toFiniteNumber(a.strike) ?? Number.MAX_SAFE_INTEGER;
      const strikeB = toFiniteNumber(b.strike) ?? Number.MAX_SAFE_INTEGER;
      if (strikeA !== strikeB) return strikeA - strikeB;

      return String(a.trading_symbol || "").localeCompare(
        String(b.trading_symbol || ""),
      );
    });
  }, [positions.open]);

  useEffect(() => {
    let isMounted = true;

    const fetchLatestPnL = async () => {
      try {
        const response = await api.get("/pnl/latest");
        if (isMounted) {
          setInitialPnL(response.data || null);
        }
      } catch {
        if (isMounted) {
          setInitialPnL(null);
        }
      }
    };

    fetchLatestPnL();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;

    let isMounted = true;

    const fetchRiskSettings = async () => {
      try {
        const settings = await getSettings();
        if (!isMounted) return;

        const exitPnl = settings.exit_pnl || {};
        const targetPnl = settings.target_pnl || {};
        const trailingStop = settings.trailing_stop || {};
        const nextTrailingPercent = trailingStop.trailing_percent || 25;
        const nextTrailingActivation = trailingStop.activation_profit || 500;

        setExitPnlEnabled(Boolean(exitPnl.enabled));
        setExitPnlValue(exitPnl.value ?? -1000);
        setTargetPnlEnabled(Boolean(targetPnl.enabled));
        setTargetPnlValue(targetPnl.value ?? 2000);
        setTrailingEnabled(Boolean(trailingStop.enabled));
        setTrailingPercent(nextTrailingPercent);
        setTrailingActivation(nextTrailingActivation);
        setTrailingText(`${nextTrailingPercent}@${nextTrailingActivation}`);
        setEditTrailingPercent(nextTrailingPercent);
        setEditTrailingActivation(nextTrailingActivation);
        setPeakPnl(trailingStop.current_peak || 0);
      } catch (error) {
        console.error("Error fetching mobile risk settings:", error);
      }
    };

    fetchRiskSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchLatestDerivedData = async () => {
      if (!selectedIndex) {
        setLatestDerivedData(null);
        return;
      }

      try {
        const response = await fetchDerivedDataLatest(selectedIndex, null);
        if (isMounted) {
          setLatestDerivedData(response?.data?.records?.[0] || null);
        }
      } catch {
        if (isMounted) {
          setLatestDerivedData(null);
        }
      }
    };

    fetchLatestDerivedData();

    return () => {
      isMounted = false;
    };
  }, [selectedIndex]);

  useEffect(() => {
    let isMounted = true;

    const fetchPositionMetadata = async () => {
      try {
        const [lotSizeData, freezeLimitData] = await Promise.all([
          getLotSizes(),
          getFreezeLimits(),
        ]);

        if (isMounted) {
          setLotSizes(lotSizeData || {});
          setFreezeLimits(freezeLimitData || {});
        }
      } catch {
        if (isMounted) {
          setLotSizes({});
          setFreezeLimits({});
        }
      }
    };

    fetchPositionMetadata();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setAddQuantities({});
    setMoveQuantities({});
    setExpandedPositionKey(null);
  }, [defaultQuantity]);

  useEffect(() => {
    if (!confirmingExit) return undefined;

    const timer = window.setTimeout(() => {
      setConfirmingExit(false);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [confirmingExit]);

  const streamPnL = toFiniteNumber(pnlStreamData?.total_pnl);
  const initialTotalPnL = toFiniteNumber(initialPnL?.total_pnl);
  const summaryPnL = toFiniteNumber(summary.total_pnl);
  const currentPnL = streamPnL ?? initialTotalPnL ?? summaryPnL ?? 0;
  const updatedAt = pnlStreamData?.updated_at ?? initialPnL?.updated_at;
  const openPositionCount = openPositions.length;
  const activeIndexData = selectedIndex ? indexStreamData?.[selectedIndex] : null;
  const spotPrice = toFiniteNumber(
    activeIndexData?.spot ??
    activeIndexData?.ltp ??
    derivedStreamData?.spot_price ??
    latestDerivedData?.index_price,
  );
  const syntheticFuture = toFiniteNumber(
    activeIndexData?.synthetic_future ??
    derivedStreamData?.synthetic_future ??
    latestDerivedData?.synthetic_future,
  );
  const compactExpiry = formatCompactExpiry(selectedExpiry);
  const marketLine = [
    selectedIndex || "Index",
    spotPrice !== null ? `S ${formatNumber(spotPrice, 2)}` : null,
    syntheticFuture !== null ? `SF ${formatNumber(syntheticFuture, 2)}` : null,
    compactExpiry ? `Exp ${compactExpiry}` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const putPendingValue = toFiniteNumber(summary.total_put_pending) ?? 0;
  const callPendingValue = toFiniteNumber(summary.total_call_pending) ?? 0;
  const totalPendingValue =
    toFiniteNumber(summary.total_pending_value) ??
    putPendingValue + callPendingValue;
  const positionsValueSummary = `PE ${formatNumber(putPendingValue, 0)} CE ${formatNumber(
    callPendingValue,
    0,
  )} T ${formatNumber(totalPendingValue, 0)}`;

  const saveExitPnl = useCallback(
    async (enabled, value) => {
      const parsedValue = parseRiskNumber(value, 0);
      setIsRiskLoading(true);

      try {
        await updatePnlSettings("exit_pnl", enabled, parsedValue);
        setExitPnlEnabled(enabled);
        setExitPnlValue(parsedValue);
        showSuccess(`Stop ${enabled ? "enabled" : "disabled"} at ${parsedValue}`);
      } catch (error) {
        showError(`Failed to update stop: ${error.message}`);
      } finally {
        setIsRiskLoading(false);
      }
    },
    [showError, showSuccess],
  );

  const saveTargetPnl = useCallback(
    async (enabled, value) => {
      const parsedValue = parseRiskNumber(value, 0);
      setIsRiskLoading(true);

      try {
        await updatePnlSettings("target_pnl", enabled, parsedValue);
        setTargetPnlEnabled(enabled);
        setTargetPnlValue(parsedValue);
        showSuccess(`Target ${enabled ? "enabled" : "disabled"} at ${parsedValue}`);
      } catch (error) {
        showError(`Failed to update target: ${error.message}`);
      } finally {
        setIsRiskLoading(false);
      }
    },
    [showError, showSuccess],
  );

  const stepExitPnl = useCallback(
    (delta) => {
      const nextValue = parseRiskNumber(exitPnlValue, 0) + delta;
      setExitPnlValue(nextValue);

      if (exitPnlEnabled) {
        saveExitPnl(true, nextValue);
      }
    },
    [exitPnlEnabled, exitPnlValue, saveExitPnl],
  );

  const stepTargetPnl = useCallback(
    (delta) => {
      const nextValue = parseRiskNumber(targetPnlValue, 0) + delta;
      setTargetPnlValue(nextValue);

      if (targetPnlEnabled) {
        saveTargetPnl(true, nextValue);
      }
    },
    [saveTargetPnl, targetPnlEnabled, targetPnlValue],
  );

  const saveTrailingStop = useCallback(
    async (enabled, textValue = trailingText) => {
      const parsed = parseTrailingText(
        textValue,
        trailingPercent,
        trailingActivation,
      );
      const trailingPercentValue = normalizeTrailingPercent(
        parsed.percent,
        trailingPercent,
      );
      const trailingActivationValue = normalizeTrailingActivation(
        parsed.activation,
        trailingActivation,
      );

      setIsRiskLoading(true);

      try {
        await updateTrailingStop(
          enabled,
          trailingPercentValue,
          trailingActivationValue,
        );
        setTrailingEnabled(enabled);
        setTrailingPercent(trailingPercentValue);
        setTrailingActivation(trailingActivationValue);
        setTrailingText(`${trailingPercentValue}@${trailingActivationValue}`);
        setEditTrailingPercent(trailingPercentValue);
        setEditTrailingActivation(trailingActivationValue);
        showSuccess(
          `Trailing ${enabled ? "enabled" : "disabled"} ${trailingPercentValue}%@${trailingActivationValue}`,
        );
        return true;
      } catch (error) {
        showError(`Failed to update trailing: ${error.message}`);
        return false;
      } finally {
        setIsRiskLoading(false);
      }
    },
    [showError, showSuccess, trailingActivation, trailingPercent, trailingText],
  );

  const openTrailingEditor = useCallback(() => {
    setEditTrailingPercent(trailingPercent);
    setEditTrailingActivation(trailingActivation);
    setShowTrailingEditor(true);
  }, [trailingActivation, trailingPercent]);

  const stepTrailingPercent = useCallback(
    (delta) => {
      setEditTrailingPercent((previousValue) =>
        normalizeTrailingPercent(
          normalizeTrailingPercent(previousValue, trailingPercent) + delta,
          trailingPercent,
        ),
      );
    },
    [trailingPercent],
  );

  const stepTrailingActivation = useCallback(
    (delta) => {
      setEditTrailingActivation((previousValue) =>
        normalizeTrailingActivation(
          normalizeTrailingActivation(previousValue, trailingActivation) + delta,
          trailingActivation,
        ),
      );
    },
    [trailingActivation],
  );

  const saveTrailingEditor = useCallback(async () => {
    const parsedPercent = normalizeTrailingPercent(
      editTrailingPercent,
      trailingPercent,
    );
    const parsedActivation = normalizeTrailingActivation(
      editTrailingActivation,
      trailingActivation,
    );
    const saved = await saveTrailingStop(
      trailingEnabled,
      `${parsedPercent}@${parsedActivation}`,
    );

    if (saved) {
      setShowTrailingEditor(false);
    }
  }, [
    editTrailingActivation,
    editTrailingPercent,
    saveTrailingStop,
    trailingActivation,
    trailingEnabled,
    trailingPercent,
  ]);

  const getConfiguredLotSizeForPosition = useCallback(
    (position) => {
      const index = getIndexFromSymbol(position.trading_symbol || selectedIndex);
      return toFiniteNumber(lotSizes[index]);
    },
    [lotSizes, selectedIndex],
  );

  const getLotSizeForPosition = useCallback(
    (position) => {
      const fallbackQuantity = toFiniteNumber(defaultQuantity);
      return getConfiguredLotSizeForPosition(position) || fallbackQuantity || 1;
    },
    [defaultQuantity, getConfiguredLotSizeForPosition],
  );

  const requireLotSizeForPosition = useCallback(
    (position) => {
      const index = getIndexFromSymbol(position.trading_symbol || selectedIndex);
      const configuredLotSize = getConfiguredLotSizeForPosition(position);

      if (!configuredLotSize) {
        throw new Error(`Lot size is still loading for ${index}. Please try again.`);
      }

      return configuredLotSize;
    },
    [getConfiguredLotSizeForPosition, selectedIndex],
  );

  const getFreezeLimitForPosition = useCallback(
    (position) => {
      const index = getIndexFromSymbol(position.trading_symbol || selectedIndex);
      const configuredFreezeLimit = toFiniteNumber(freezeLimits[index]);

      return (
        configuredFreezeLimit ||
        DEFAULT_FREEZE_LIMITS[index] ||
        getLotSizeForPosition(position) * 10
      );
    },
    [freezeLimits, getLotSizeForPosition, selectedIndex],
  );

  const generateQuantityOptions = useCallback((lotSize, freezeLimit) => {
    const normalizedLotSize = toFiniteNumber(lotSize) || 1;
    const normalizedFreezeLimit =
      toFiniteNumber(freezeLimit) || normalizedLotSize;
    const maxLots = Math.max(
      1,
      Math.floor(normalizedFreezeLimit / normalizedLotSize),
    );
    const options = new Set();

    for (let i = 1; i <= Math.min(5, maxLots); i += 1) {
      options.add(i * normalizedLotSize);
    }

    for (let i = 10; i <= maxLots; i += 5) {
      options.add(i * normalizedLotSize);
    }

    options.add(normalizedFreezeLimit);

    return Array.from(options).sort((a, b) => a - b);
  }, []);

  const selectedIndexKey = getIndexFromSymbol(selectedIndex);
  const globalLotSize =
    toFiniteNumber(lotSizes[selectedIndexKey]) ||
    toFiniteNumber(defaultQuantity) ||
    1;
  const globalFreezeLimit =
    toFiniteNumber(freezeLimits[selectedIndexKey]) ||
    DEFAULT_FREEZE_LIMITS[selectedIndexKey] ||
    globalLotSize * 10;
  const globalQuantitySuggestions = generateQuantityOptions(
    globalLotSize,
    globalFreezeLimit,
  );

  const handleGlobalQuantityChange = useCallback(
    (value) => {
      const parsedValue = parseInt(value, 10);
      if (!Number.isFinite(parsedValue)) return;
      onDefaultQuantityChange?.(parsedValue);
    },
    [onDefaultQuantityChange],
  );

  const getAddQuantity = useCallback(
    (position) => {
      const key = getPositionIdentity(position);
      const selectedQuantity = toFiniteNumber(addQuantities[key]);

      if (selectedQuantity && selectedQuantity > 0) {
        return selectedQuantity;
      }

      const lotSize = getLotSizeForPosition(position);
      const fallbackQuantity = toFiniteNumber(defaultQuantity) || lotSize;
      return Math.max(lotSize, fallbackQuantity);
    },
    [addQuantities, defaultQuantity, getLotSizeForPosition],
  );

  const handleAddQuantityChange = useCallback((position, value) => {
    const parsedValue = parseInt(value, 10);
    if (!Number.isFinite(parsedValue)) return;

    const key = getPositionIdentity(position);
    setAddQuantities((previous) => ({
      ...previous,
      [key]: parsedValue,
    }));
  }, []);

  const getMoveQuantity = useCallback(
    (position, targetStrike) => {
      const key = getMoveQuantityKey(position, targetStrike);
      const selectedQuantity = toFiniteNumber(moveQuantities[key]);

      if (selectedQuantity && selectedQuantity > 0) {
        return selectedQuantity;
      }

      const lotSize = getLotSizeForPosition(position);
      const openQuantity = Math.abs(toFiniteNumber(position.quantity) ?? 0);
      const fallbackQuantity = toFiniteNumber(defaultQuantity) || lotSize;
      const quantity = Math.max(lotSize, fallbackQuantity);

      if (openQuantity > 0) {
        return Math.min(quantity, openQuantity);
      }

      return quantity;
    },
    [defaultQuantity, getLotSizeForPosition, moveQuantities],
  );

  const handleMoveQuantityChange = useCallback((position, targetStrike, value) => {
    const parsedValue = parseInt(value, 10);
    if (!Number.isFinite(parsedValue)) return;

    const key = getMoveQuantityKey(position, targetStrike);
    setMoveQuantities((previous) => ({
      ...previous,
      [key]: parsedValue,
    }));
  }, []);

  const getFractionalQuantity = useCallback(
    (position, stepSize) => {
      const lotSize = getLotSizeForPosition(position);
      const openQuantity = Math.abs(toFiniteNumber(position.quantity) ?? 0);
      const fullLots = Math.floor(openQuantity / lotSize);
      const targetLots = Math.floor(fullLots * getStepFraction(stepSize));

      return targetLots * lotSize;
    },
    [getLotSizeForPosition],
  );

  const validateAddQuantity = useCallback(
    (position, quantity) => {
      const lotSize = requireLotSizeForPosition(position);
      const parsedQuantity = Number(quantity);

      if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
        throw new Error("Quantity must be positive.");
      }

      if (parsedQuantity < lotSize) {
        throw new Error(`Quantity must be at least one lot (${lotSize}).`);
      }

      if (parsedQuantity % lotSize !== 0) {
        throw new Error(
          `Quantity ${parsedQuantity} must be a multiple of lot size ${lotSize}.`,
        );
      }

      return parsedQuantity;
    },
    [requireLotSizeForPosition],
  );

  const validateMoveQuantity = useCallback(
    (position, quantity) => {
      const lotSize = requireLotSizeForPosition(position);
      const parsedQuantity = Number(quantity);

      if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
        throw new Error("Quantity must be positive.");
      }

      if (parsedQuantity < lotSize) {
        throw new Error(`Quantity must be at least one lot (${lotSize}).`);
      }

      if (parsedQuantity % lotSize !== 0) {
        throw new Error(
          `Quantity ${parsedQuantity} must be a multiple of lot size ${lotSize}.`,
        );
      }

      const openQuantity = Math.abs(toFiniteNumber(position.quantity) ?? 0);
      if (parsedQuantity > openQuantity) {
        throw new Error(
          `Quantity ${parsedQuantity} exceeds open position quantity ${openQuantity}.`,
        );
      }

      return parsedQuantity;
    },
    [requireLotSizeForPosition],
  );

  const handleAddPosition = useCallback(
    async (position, quantity) => {
      const actionKey = getAddActionKey(position);
      setMovingKey(actionKey);

      try {
        if (!position.instrument_token) {
          throw new Error("Instrument token is missing for the position.");
        }

        const parsedQuantity = validateAddQuantity(position, quantity);
        const action = (toFiniteNumber(position.quantity) ?? 0) > 0 ? "BUY" : "SELL";
        const data = await placeOrder({
          strike: position.strike,
          optionType: position.option_type,
          action,
          quantity: parsedQuantity,
          index: getIndexFromSymbol(position.trading_symbol || selectedIndex),
          expiry: position.expiry,
          instrumentToken: position.instrument_token,
          tradingSymbol: position.trading_symbol,
          tag: `Add_Position_${position.strike}_${position.option_type}`,
        });
        const firstStatus = Array.isArray(data) ? data[0]?.status : null;
        const success =
          data?.success ||
          (Array.isArray(data) &&
            data.length > 0 &&
            ["success", "PLACED", "COMPLETE", "EXECUTED"].includes(firstStatus));

        if (!success) {
          throw new Error(data?.message || "Add order failed");
        }

        showSuccess(formatOrderSuccess(action, parsedQuantity, position.trading_symbol));
        eventBus.emit(EVENTS.ORDER_PLACED_SUCCESS, {
          action,
          quantity: parsedQuantity,
          tradingSymbol: position.trading_symbol,
        });

        try {
          await onRefreshPositions?.();
        } catch {
          showWarning("Add submitted, but positions refresh failed");
        }
      } catch (error) {
        const errorMessage = formatOrderError(error);
        showError(errorMessage);

        eventBus.emit(EVENTS.FAILED_ORDER_CREATED, {
          strike: position.strike,
          optionType: position.option_type,
          action: (toFiniteNumber(position.quantity) ?? 0) > 0 ? "BUY" : "SELL",
          quantity: parseInt(quantity, 10),
          error: errorMessage,
        });
      } finally {
        setMovingKey(null);
      }
    },
    [
      onRefreshPositions,
      selectedIndex,
      showError,
      showSuccess,
      showWarning,
      validateAddQuantity,
    ],
  );

  const handleMovePosition = useCallback(
    async (position, direction, targetStrike, quantity) => {
      const actionKey = getMoveActionKey(position, direction, targetStrike);
      setMovingKey(actionKey);

      try {
        if (!position.instrument_token) {
          throw new Error("Instrument token is missing for the position.");
        }

        const parsedQuantity = validateMoveQuantity(position, quantity);
        const data = await placeMoveOrder(
          position,
          direction,
          targetStrike,
          parsedQuantity,
        );
        const firstStatus = Array.isArray(data) ? data[0]?.status : null;
        const success =
          data?.success ||
          (Array.isArray(data) &&
            data.length > 0 &&
            ["success", "PLACED", "COMPLETE", "EXECUTED"].includes(firstStatus));

        if (!success) {
          throw new Error(data?.message || "Move order failed");
        }

        showSuccess(
          `MOVE ${direction.toUpperCase()} order placed: ${parsedQuantity} x ${position.trading_symbol}`,
        );
      } catch (error) {
        const errorMessage = formatOrderError(error);
        showError(errorMessage);

        eventBus.emit(EVENTS.FAILED_ORDER_CREATED, {
          strike: position.strike,
          optionType: position.option_type,
          action: direction === "away" ? "MOVE_AWAY" : "MOVE_CLOSER",
          quantity: parseInt(quantity, 10),
          targetStrike,
          error: errorMessage,
        });
      } finally {
        setMovingKey(null);
      }
    },
    [showError, showSuccess, validateMoveQuantity],
  );

  const handleExitAll = useCallback(async () => {
    if (openPositionCount === 0) {
      showWarning("No open positions to exit");
      return;
    }

    if (!confirmingExit) {
      setConfirmingExit(true);
      showWarning("Tap Exit All again within 5 seconds to confirm");
      return;
    }

    setIsExiting(true);
    setConfirmingExit(false);

    try {
      const result = await placeExitAllOrders();
      showSuccess(result?.message || "Exit All submitted");

      try {
        await onRefreshPositions?.();
      } catch {
        showWarning("Exit submitted, but positions refresh failed");
      }
    } catch (error) {
      showError(error?.message || "Failed to exit all positions");
    } finally {
      setIsExiting(false);
    }
  }, [
    confirmingExit,
    onRefreshPositions,
    openPositionCount,
    showError,
    showSuccess,
    showWarning,
  ]);

  return (
    <div className="mobile-home-view">
      <section className="mobile-home-pnl-panel" aria-label="Current PnL">
        <div className="mobile-home-pnl-main">
          <div>
            <div className="mobile-home-eyebrow">Current P&L</div>
            <div
              className={`mobile-home-pnl-value ${
                currentPnL >= 0 ? "is-profit" : "is-loss"
              }`}
            >
              {formatSignedAmount(currentPnL)}
            </div>
          </div>
          <div className="mobile-home-actions">
            <label className="mobile-global-quantity-control">
              <span>Q</span>
              <QuantityInput
                value={defaultQuantity}
                onChange={handleGlobalQuantityChange}
                lotSize={globalLotSize}
                freezeLimit={globalFreezeLimit}
                suggestions={globalQuantitySuggestions}
                containerClassName="mobile-global-quantity"
                compact={true}
              />
            </label>
            <button
              type="button"
              className={`mobile-exit-all-button ${
                confirmingExit ? "is-confirming" : ""
              }`}
              onClick={handleExitAll}
              disabled={isExiting || openPositionCount === 0}
            >
              {isExiting
                ? "Exiting..."
                : confirmingExit
                  ? "Tap again"
                  : "Exit All"}
            </button>
          </div>
        </div>

        <div className="mobile-home-pnl-meta">
          <span>{marketLine}</span>
          <span>{isConnected ? "Live" : "Offline"} - {formatISTTime(updatedAt)} IST</span>
        </div>

        <MobileRiskSettings
          disabled={isRiskLoading}
          exitPnlEnabled={exitPnlEnabled}
          exitPnlValue={exitPnlValue}
          targetPnlEnabled={targetPnlEnabled}
          targetPnlValue={targetPnlValue}
          trailingEnabled={trailingEnabled}
          trailingText={trailingText}
          peakPnl={peakPnl}
          onExitToggle={(enabled) => saveExitPnl(enabled, exitPnlValue)}
          onExitChange={setExitPnlValue}
          onExitBlur={() => {
            const parsedValue = parseRiskNumber(exitPnlValue, 0);
            setExitPnlValue(parsedValue);
            if (exitPnlEnabled) {
              saveExitPnl(true, parsedValue);
            }
          }}
          onExitStep={stepExitPnl}
          onTargetToggle={(enabled) => saveTargetPnl(enabled, targetPnlValue)}
          onTargetChange={setTargetPnlValue}
          onTargetBlur={() => {
            const parsedValue = parseRiskNumber(targetPnlValue, 0);
            setTargetPnlValue(parsedValue);
            if (targetPnlEnabled) {
              saveTargetPnl(true, parsedValue);
            }
          }}
          onTargetStep={stepTargetPnl}
          onTrailingToggle={(enabled) => saveTrailingStop(enabled)}
          onTrailingOpen={openTrailingEditor}
        />
        {showTrailingEditor && (
          <MobileTrailingEditor
            disabled={isRiskLoading}
            percent={editTrailingPercent}
            activation={editTrailingActivation}
            onPercentChange={setEditTrailingPercent}
            onActivationChange={setEditTrailingActivation}
            onPercentStep={stepTrailingPercent}
            onActivationStep={stepTrailingActivation}
            onCancel={() => setShowTrailingEditor(false)}
            onSave={saveTrailingEditor}
          />
        )}
      </section>

      <section className="mobile-home-positions" aria-label="Open positions">
        <div className="mobile-home-section-header">
          <span className="mobile-home-section-title">
            <span>Positions</span>
            <span className="mobile-home-section-values">
              ({positionsValueSummary})
            </span>
          </span>
          <span>{openPositionCount} open</span>
        </div>

        {openPositionCount > 0 ? (
          <div className="mobile-positions-table-wrap">
            <table className="mobile-positions-table">
              <thead>
                <tr>
                  <th scope="col">Leg</th>
                  <th scope="col">Qty</th>
                  <th scope="col">LTP</th>
                  <th scope="col">Diff</th>
                </tr>
              </thead>
              <tbody>
                {openPositions.map((position, index) => (
                  <MobilePositionRow
                    key={
                      position.instrument_token ||
                      position.trading_symbol ||
                      `${position.strike}-${position.option_type}-${index}`
                    }
                    position={position}
                    isExpanded={expandedPositionKey === getPositionIdentity(position)}
                    onToggleMovement={() => {
                      const positionKey = getPositionIdentity(position);
                      setExpandedPositionKey((previousKey) =>
                        previousKey === positionKey ? null : positionKey,
                      );
                    }}
                    getAddQuantity={getAddQuantity}
                    onAddQuantityChange={handleAddQuantityChange}
                    onAddPosition={handleAddPosition}
                    getMoveQuantity={getMoveQuantity}
                    onMoveQuantityChange={handleMoveQuantityChange}
                    onMovePosition={handleMovePosition}
                    movingKey={movingKey}
                    getLotSizeForPosition={getLotSizeForPosition}
                    getFreezeLimitForPosition={getFreezeLimitForPosition}
                    generateQuantityOptions={generateQuantityOptions}
                    getFractionalQuantity={getFractionalQuantity}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mobile-home-empty">No open positions</div>
        )}
      </section>
    </div>
  );
}
